"use client";

import { useEffect } from "react";
import { useSearchParams, usePathname } from "next/navigation";

/**
 * All URL-based tracking parameters to capture from paid media campaigns.
 */
const TRACKING_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "li_fat_id",
  "ttclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "hsa_acc",
  "hsa_cam",
  "hsa_grp",
  "hsa_ad",
  "hsa_src",
  "hsa_tgt",
  "hsa_kw",
  "hsa_mt",
  "hsa_net",
  "hsa_ver"
];

const FIRST_TOUCH_KEY = "tracking_first_touch";
const LAST_TOUCH_KEY = "tracking_last_touch";
const PREVIOUS_PAGE_KEY = "tracking_previous_page";

/**
 * Captures tracking/attribution parameters from the URL on landing.
 *
 * Strategy:
 * - First-touch: Written once on first arrival with tracking params. Never overwritten.
 * - Last-touch: Updated every time the user arrives with new tracking params.
 * - Previous page: Tracked on every route change so we can use it as referrer fallback.
 *
 * After capturing, tracking params are stripped from the URL for clean SEO URLs.
 *
 * To retrieve stored data, use helpers from `@/utils/trackingAttributes`.
 */
export default function TrackingCapture() {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Track previous page on every route change (for referrer fallback)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentPage = window.location.origin + window.location.pathname;

    return () => {
      // When navigating away, store current page as previous page
      try {
        sessionStorage.setItem(PREVIOUS_PAGE_KEY, currentPage);
      } catch {
        // sessionStorage unavailable — silently skip
      }
    };
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // landing_page = clean URL (no query params)
    // original_landing_page = full URL with all query params
    const cleanLandingUrl = window.location.origin + window.location.pathname;
    const fullLandingUrl = window.location.href;
    const referrer = document.referrer || "";

    // Collect tracking params present in the current URL
    const captured = {};
    let hasTracking = false;

    if (searchParams && searchParams.size > 0) {
      TRACKING_PARAMS.forEach((param) => {
        const value = searchParams.get(param);
        if (value) {
          captured[param] = value;
          hasTracking = true;
        }
      });
    }

    // Only proceed if we actually have tracking params in the URL
    if (!hasTracking) return;

    const touchData = {
      ...captured,
      landing_page: cleanLandingUrl,
      original_landing_page: fullLandingUrl,
      referrer,
      _timestamp: Date.now()
    };

    try {
      // First-touch: written once, never overwritten
      if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
        localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touchData));
      }

      // Last-touch: updated on every arrival with tracking params
      localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(touchData));
    } catch {
      // localStorage unavailable — silently skip
    }

    // Strip tracking params from URL without page reload
    const newParams = new URLSearchParams(searchParams.toString());
    TRACKING_PARAMS.forEach((param) => newParams.delete(param));

    const cleanSearch = newParams.toString();
    const cleanUrl = cleanSearch ? `${pathname}?${cleanSearch}` : pathname;

    window.history.replaceState(window.history.state, "", cleanUrl);
  }, [searchParams, pathname]);

  return null;
}
